import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { useCallback, useState } from 'react';

import type { FolderFile } from './clientCard.model';
import { fetchFolderFileBlob, folderFileKey } from './folderFile';

/** Long enough for Chrome to have written the file out of the blob. */
const RELEASE_AFTER_MS = 60_000;

/**
 * Downloading a file from the client folder, whichever side it hangs off.
 *
 * In flight is a set of files rather than a single id, because a reader can start a
 * second download before the first comes back: with one id, whichever finished first
 * cleared the other's spinner too.
 */
const useFolderDownload = () => {
  const { handleError } = useSmartErrorHandling();
  const [inFlight, setInFlight] = useState<string[]>([]);

  const download = useCallback(
    async (file: FolderFile) => {
      const key = folderFileKey(file);
      setInFlight((keys) => (keys.includes(key) ? keys : [...keys, key]));
      try {
        const url = URL.createObjectURL(await fetchFolderFileBlob(file));
        const link = document.createElement('a');
        link.href = url;
        link.download = file.attachment.filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        // The link and the URL are left standing: Chrome reads the blob after the
        // click returns, and tearing either down while it is still writing leaves the
        // download sitting there for seconds or dropping it altogether.
        setTimeout(() => {
          link.remove();
          URL.revokeObjectURL(url);
        }, RELEASE_AFTER_MS);
      } catch (error) {
        handleError(error as Error);
      } finally {
        setInFlight((keys) => keys.filter((one) => one !== key));
      }
    },
    [handleError]
  );

  const isDownloading = useCallback((file: FolderFile) => inFlight.includes(folderFileKey(file)), [inFlight]);

  return { download, isDownloading };
};

export default useFolderDownload;
